<!--
  @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
  @license AGPL-3.0

  This code is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as published by
  the Free Software Foundation, either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.

  Any use of the work other than as authorized under this license or copyright law is prohibited.
-->

<template>
  <nav
    ref="navRef"
    class="sidebar flex-col py-24 px-8 h-full"
    role="navigation"
    :aria-label="i18n.navLabel"
    data-orientation="vertical"
  >
    <ul class="steps ps-0">
      <li
        v-for="{ id, sidebarLabel, icon, disabled } in sidebarSteps" :key="id"
        :class="{ current: currentId === id }"
        :aria-current="currentId === id ? 'step' : null"
        class="step mb-8 rounded"
      >
        <button @click="$emit('changePage', id)" :disabled="disabled" :tabindex="currentId === id ? 0 : -1" class="w-full flex-col btn btn-toolbar py-12 px-2">
          <div aria-hidden="true" class="sidebar-icon mb-4">
            <BaseIcon :icon-name="icon" class="bi-20"></BaseIcon>
          </div>
          <span class="sidebar-label ellipsis text-xxs break-word whitespace-normal">{{ sidebarLabel }}</span>
        </button>
      </li>
    </ul>
  </nav>
</template>

<script setup>
import { ref } from 'vue'
import BaseIcon from '@/io.ox/vueComponents/base-icon.vue'
import { useKeyboardNavigation } from '@/io.ox/mail/settings/signatures/signature-designer/composables/useKeyboardNavigation'

import gt from 'gettext'

const navRef = ref(null)
useKeyboardNavigation(navRef, 'vertical')

const { sidebarSteps, currentId } = defineProps({
  sidebarSteps: { type: Object, required: true },
  currentId: { type: String, required: false }
})

const i18n = {
  navLabel: gt('Designer steps')
}

defineEmits(['changePage'])
</script>

<style lang="scss" scoped>
.btn.btn-toolbar {
  color: var(--text);
  &:focus {
    background-color: var(--focus-500);
    color: var(--selected-focus);
  }
}

.sidebar {
  width: 112px;
  border-right: 1px solid var(--border);

  .steps {
    .step {
      display: block;
      &.current { background-color: var(--selected-background); }
    }

    .sidebar-label {
      hyphens: auto;
    }
  }
}

@media (max-width: 1024px) {
  .sidebar { display: none; }
}
</style>
